import {
    Firestore,
    collection,
    query,
    where,
    getDocs,
    Timestamp,
    doc,
    getDoc,
    getCountFromServer,
    getAggregateFromServer,
    sum,
    Query,
    DocumentData,
    QuerySnapshot,
    orderBy,
    documentId
} from 'firebase/firestore';
import {
    differenceInDays,
    format,
    startOfWeek,
    endOfWeek,
    parse,
    compareAsc,
    getWeek,
    startOfMonth,
    endOfMonth,
    addWeeks
} from 'date-fns';
import { stat } from 'fs';

interface User {
    companyId: string;
}

interface UserData {
    id: string;
    callAttemptTarget?: number; // Add this line to include callAttemptTarget property
    // Add other user properties as needed
}


// Function to fetch call data from calllogs collection

interface ChartData {
    time: string;
    total: number;
    staff?: number;
}

interface FetchCallDataParams {
    db: Firestore;
    userData: User | null;
    startTime: string;
    endTime: string;
    users: UserData[];
    currentScreen: string;
    selectedUser: UserData[] | null;
}

interface CallLog {
    callTimestamp: Date;
    userId: string;
    // Add other call log properties as needed
}

export async function fetchCallData({
    db,
    userData,
    startTime,
    endTime,
    users,
    currentScreen,
    selectedUser
}: FetchCallDataParams): Promise<ChartData[]> {
    if (!userData?.companyId) {
        return [];
    }

    try {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const start = new Date(today);
        const [startHour, startMinute] = startTime.split(':').map(Number);
        start.setHours(startHour, startMinute, 0, 0);
        const end = new Date(today);
        const [endHour, endMinute] = endTime.split(':').map(Number);
        end.setHours(endHour, endMinute, 0, 0);
        const startTimestamp = Timestamp.fromDate(start);
        const endTimestamp = Timestamp.fromDate(end);

        let callLogsRef = collection(db, `companies/${userData.companyId}/callLogs`);
        let timeQuery = query(
            callLogsRef,
            where('callTimestamp', '>=', startTimestamp),
            where('callTimestamp', '<', endTimestamp)
        );

        // Apply user filters based on screen and selected users
        if (currentScreen === 'Manager' && users.length > 0) {
            console.log("Manager", users);

            timeQuery = query(
                callLogsRef,
                where('callTimestamp', '>=', startTimestamp),
                where('callTimestamp', '<', endTimestamp),
                where('userId', 'in', users.map(user => user.id))
            );
        } else if (currentScreen === 'Staff' || users.length === 1) {
            console.log("Staff", users);

            timeQuery = query(
                callLogsRef,
                where('callTimestamp', '>=', startTimestamp),
                where('callTimestamp', '<', endTimestamp),
                where('userId', '==', users[0].id)
            );
        }

        const querySnapshot = await getDocs(timeQuery);
        const calls = querySnapshot.docs.map(doc => ({
            ...doc.data(),
            callTimestamp: doc.data()?.callTimestamp.toDate()
        })) as CallLog[];

        const hourlyCalls = new Array(24).fill(0);

        calls.forEach(call => {
            const hour = call.callTimestamp.getHours();
            hourlyCalls[hour]++;
        });

        const startHourIndex = start.getHours();
        const endHourIndex = end.getHours();
        const filteredHourlyCalls = hourlyCalls.slice(startHourIndex, endHourIndex + 1);

        // Transform the data
        let transformedData: ChartData[] = filteredHourlyCalls.map((calls, index) => {
            const hour = startHourIndex + index;
            return {
                time: formatHour(hour),
                total: calls
            };
        });

        // Only fetch and add staff data if a user is selected
        if (selectedUser && selectedUser.length > 0) {
            const staffQuery = query(
                callLogsRef,
                where('callTimestamp', '>=', startTimestamp),
                where('callTimestamp', '<', endTimestamp),
                where('userId', '==', selectedUser[0].id)
            );

            const staffSnapshot = await getDocs(staffQuery);
            const staffCalls = staffSnapshot.docs.map(doc => ({
                ...doc.data(),
                callTimestamp: doc.data()?.callTimestamp.toDate()
            })) as CallLog[];

            const staffHourlyCalls = new Array(24).fill(0);

            staffCalls.forEach(call => {
                const hour = call.callTimestamp.getHours();
                staffHourlyCalls[hour]++;
            });

            const filteredStaffCalls = staffHourlyCalls.slice(startHourIndex, endHourIndex + 1);

            // Add staff data to the transformed data
            transformedData = transformedData.map((data, index) => ({
                ...data,
                staff: filteredStaffCalls[index]
            }));
        }

        return transformedData;

    } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Error fetching call logs';
        console.error("Error fetching call logs:", error);
        throw new Error(errorMessage);
    }
}

// Helper function to format hours
function formatHour(hour: number): string {
    const period = hour >= 12 ? 'PM' : 'AM';
    const adjustedHour = hour % 12 || 12;
    return `${adjustedHour}${period}`;
}


// Function to fetch call metrics analytics from calllogs collection
interface FetchCallMetricsParams {
    db: Firestore;
    userData: any;
    startTime: string | null;
    endTime: string | null;
    users: UserData[];
    currentScreen: string;
    selectedUser: UserData[] | null;
}

interface CallMetricsResponse {
    outboundCalls: number;
    callAttemptTarget: number;
    missedCalls: number;
    connected: number;
    inboundCalls: number;
    outboundDuration: number;  // in seconds
    inboundDuration: number;   // in seconds
    totalDuration: number;     // in seconds
    durationZero: number;
    durationNotZero: number;
}

export async function fetchCallMetrics({
    db,
    userData,
    startTime,
    endTime,
    users,
    currentScreen,
    selectedUser
}: FetchCallMetricsParams): Promise<CallMetricsResponse> {
    if (!userData?.companyId) {
        throw new Error('No company ID found');
    }
    try {
        const callLogsRef = collection(db, `companies/${userData?.companyId}/callLogs`);

        // Calculate days in range
        let daysInRange = 1;
        if (startTime !== null && endTime !== null) {
            const startDate = new Date(startTime);
            const endDate = new Date(endTime);
            daysInRange = differenceInDays(endDate, startDate) + 1; // +1 to include both dates
        }

        // Helper function to get user IDs based on filtering logic
        const getUserIds = (): string[] => {
            if (selectedUser && selectedUser.length > 0) {
                return selectedUser.map(user => user.id);
            } else if (currentScreen === "Manager" && users.length > 0) {
                return users.map(user => user.id);
            } else if (currentScreen === 'Staff' && users.length === 1) {
                return [users[0].id];
            }
            return [];
        };

        const userIds = getUserIds();

        // Helper function to create base query with time filtering
        const createBaseQuery = (): Query<DocumentData> => {
            let baseQuery: Query<DocumentData> = query(callLogsRef);
            
            if (startTime !== null && endTime !== null) {
                const startTimestamp = Timestamp.fromDate(new Date(startTime));
                const endTimestamp = Timestamp.fromDate(new Date(endTime));
                baseQuery = query(
                    callLogsRef,
                    where('callTimestamp', '>=', startTimestamp),
                    where('callTimestamp', '<=', endTimestamp)
                );
            }
            
            return baseQuery;
        };

        // Helper function to execute queries in batches for large user sets
        const executeQueryInBatches = async (queryBuilder: (userIdBatch: string[]) => Query<DocumentData>): Promise<number> => {
            if (userIds.length === 0) {
                // No user filtering needed
                return (await getCountFromServer(queryBuilder([]))).data().count || 0;
            }
            
            if (userIds.length <= 30) {
                // Can use single query
                return (await getCountFromServer(queryBuilder(userIds))).data().count || 0;
            }
            
            // Need to batch process
            const batchSize = 30;
            let totalCount = 0;
            
            for (let i = 0; i < userIds.length; i += batchSize) {
                const batch = userIds.slice(i, i + batchSize);
                const count = (await getCountFromServer(queryBuilder(batch))).data().count || 0;
                totalCount += count;
            }
            
            return totalCount;
        };

        // Helper function to execute aggregation queries in batches
        const executeAggregationInBatches = async (queryBuilder: (userIdBatch: string[]) => Query<DocumentData>): Promise<number> => {
            if (userIds.length === 0) {
                // No user filtering needed
                const result = await getAggregateFromServer(queryBuilder([]), { totalDuration: sum('duration') });
                return result.data().totalDuration || 0;
            }
            
            if (userIds.length <= 30) {
                // Can use single query
                const result = await getAggregateFromServer(queryBuilder(userIds), { totalDuration: sum('duration') });
                return result.data().totalDuration || 0;
            }
            
            // Need to batch process
            const batchSize = 30;
            let totalDuration = 0;
            
            for (let i = 0; i < userIds.length; i += batchSize) {
                const batch = userIds.slice(i, i + batchSize);
                const result = await getAggregateFromServer(queryBuilder(batch), { totalDuration: sum('duration') });
                totalDuration += result.data().totalDuration || 0;
            }
            
            return totalDuration;
        };

        // Execute all metrics queries
        const [outboundCalls, missed, contacted, inbound, durationZero, durationNotZero] = await Promise.all([
            executeQueryInBatches((userIdBatch) => {
                const baseQuery = createBaseQuery();
                const queryWithType = query(baseQuery, where('type', '==', 'OUTGOING'));
                return userIdBatch.length > 0 ? query(queryWithType, where('userId', 'in', userIdBatch)) : queryWithType;
            }),
            executeQueryInBatches((userIdBatch) => {
                const baseQuery = createBaseQuery();
                const queryWithType = query(baseQuery, where('type', '==', 'MISSED'));
                return userIdBatch.length > 0 ? query(queryWithType, where('userId', 'in', userIdBatch)) : queryWithType;
            }),
            executeQueryInBatches((userIdBatch) => {
                const baseQuery = createBaseQuery();
                const queryWithConditions = query(
                    baseQuery,
                    where('type', '==', 'OUTGOING'),
                    where('duration', '>', 0)
                );
                return userIdBatch.length > 0 ? query(queryWithConditions, where('userId', 'in', userIdBatch)) : queryWithConditions;
            }),
            executeQueryInBatches((userIdBatch) => {
                const baseQuery = createBaseQuery();
                const queryWithConditions = query(
                    baseQuery,
                    where('type', '==', 'INCOMING'),
                    where('duration', '>', 0)
                );
                return userIdBatch.length > 0 ? query(queryWithConditions, where('userId', 'in', userIdBatch)) : queryWithConditions;
            }),
            executeQueryInBatches((userIdBatch) => {
                const baseQuery = createBaseQuery();
                const queryWithDuration = query(baseQuery, where('duration', '==', 0));
                return userIdBatch.length > 0 ? query(queryWithDuration, where('userId', 'in', userIdBatch)) : queryWithDuration;
            }),
            executeQueryInBatches((userIdBatch) => {
                const baseQuery = createBaseQuery();
                const queryWithDuration = query(baseQuery, where('duration', '>', 0));
                return userIdBatch.length > 0 ? query(queryWithDuration, where('userId', 'in', userIdBatch)) : queryWithDuration;
            })
        ]);

        // Duration queries for outbound and inbound calls
        const [outboundDuration, inboundDuration] = await Promise.all([
            executeAggregationInBatches((userIdBatch) => {
                const baseQuery = createBaseQuery();
                const queryWithConditions = query(
                    baseQuery,
                    where('type', '==', 'OUTGOING'),
                    where('duration', '>', 0)
                );
                return userIdBatch.length > 0 ? query(queryWithConditions, where('userId', 'in', userIdBatch)) : queryWithConditions;
            }),
            executeAggregationInBatches((userIdBatch) => {
                const baseQuery = createBaseQuery();
                const queryWithConditions = query(
                    baseQuery,
                    where('type', '==', 'INCOMING'),
                    where('duration', '>', 0)
                );
                return userIdBatch.length > 0 ? query(queryWithConditions, where('userId', 'in', userIdBatch)) : queryWithConditions;
            })
        ]);

        // Get call attempt target from user collection
        const userRef = collection(db, 'users');
        let callAttemptTarget = 0;

        if (userIds.length > 0) {
            if (userIds.length <= 30) {
                const userQuery = query(
                    userRef,
                    where('companyId', '==', userData?.companyId),
                    where(documentId(), 'in', userIds)
                );
                const userSnapshot = await getDocs(userQuery);
                const userDataArray = userSnapshot.docs.map(doc => ({
                    id: doc.id,
                    callAttemptTarget: doc.data().callAttemptTarget || 0
                }));
                callAttemptTarget = userDataArray.reduce((acc, user) => {
                    return acc + (user.callAttemptTarget || 0) * daysInRange;
                }, 0);
            } else {
                // Batch process user queries too
                const batchSize = 30;
                let totalTarget = 0;
                
                for (let i = 0; i < userIds.length; i += batchSize) {
                    const batch = userIds.slice(i, i + batchSize);
                    const userQuery = query(
                        userRef,
                        where('companyId', '==', userData?.companyId),
                        where(documentId(), 'in', batch)
                    );
                    const userSnapshot = await getDocs(userQuery);
                    const userDataArray = userSnapshot.docs.map(doc => ({
                        id: doc.id,
                        callAttemptTarget: doc.data().callAttemptTarget || 0
                    }));
                    totalTarget += userDataArray.reduce((acc, user) => {
                        return acc + (user.callAttemptTarget || 0) * daysInRange;
                    }, 0);
                }
                callAttemptTarget = totalTarget;
            }
        } else {
            // No user filtering, get all users in company
            const userQuery = query(userRef, where('companyId', '==', userData?.companyId));
            const userSnapshot = await getDocs(userQuery);
            const userDataArray = userSnapshot.docs.map(doc => ({
                id: doc.id,
                callAttemptTarget: doc.data().callAttemptTarget || 0
            }));
            callAttemptTarget = userDataArray.reduce((acc, user) => {
                return acc + (user.callAttemptTarget || 0) * daysInRange;
            }, 0);
        }

        return {
            outboundCalls: outboundCalls || 0,
            callAttemptTarget: callAttemptTarget,
            missedCalls: missed || 0,
            connected: contacted || 0,
            inboundCalls: inbound || 0,
            outboundDuration: outboundDuration || 0,
            inboundDuration: inboundDuration || 0,
            totalDuration: (outboundDuration || 0) + (inboundDuration || 0),
            durationZero: durationZero || 0,
            durationNotZero: durationNotZero || 0
        };
    } catch (error) {
        throw new Error(error instanceof Error ? error.message : 'Error fetching metrics');
    }
}

// Function to fetch lead data from leads collection
interface FetchLeadDataParams {
    db: Firestore;
    userData: User | null;
    startTime: string | null;
    endTime: string | null;
    users: UserData[];
    currentScreen: string;
    selectedUser?: UserData[] | null;
    settingsData?: any;
    stageParameter?: string;
    sourceParameter?: string;
}

export interface LeadData {
    chartData?: any[];
    totalLeads: number;
    stageMapArray?: { stageId: string, stage: string, count: number, percentage: string }[];
    admissionMapArray?: { admissionStatusId: string, admissionStatus: string, count: number, percentage: string, level: number }[];
    sourceMapArray?: { sourceId: string, source: string, count: number, potentialCount: number, percentage: string, potentialpercentage: string }[];
    campaignMapArray?: {
        campaignId: string, campaign: string, count: number, statePercentageData:
        { stateId: string, state: string, count: number, percentage: string, color: string }[]
    }[];
    timelineMapArray?: { timeline: string, totalCount: number, stageId?: string, stage?: string, stageCount?: number }[];
}


export async function fetchLeadData({
    db,
    userData,
    startTime,
    endTime,
    users,
    currentScreen,
    selectedUser,
    stageParameter,
    sourceParameter
}: FetchLeadDataParams): Promise<LeadData> {
    if (!userData?.companyId) {
        throw new Error('No company ID found');
    }
    try {
        const leadsRef = collection(db, `companies/${userData?.companyId}/leads`);
        const stageRef = collection(db, `companies/${userData?.companyId}/leadStages`);
        const admissionRef = collection(db, `companies/${userData?.companyId}/leadAdmissionStatus`);
        const sourceRef = collection(db, `companies/${userData?.companyId}/leadSources`);
        const campaignRef = collection(db, `companies/${userData?.companyId}/leadCampaigns`);
        const stateRef = collection(db, `companies/${userData?.companyId}/leadStates`);
        // Create base query without stage parameter
        let baseQuery: Query<DocumentData> = query(
            leadsRef,
        );


        let startTimestamp: Timestamp | null = null;
        let endTimestamp: Timestamp | null = null;

        if (startTime !== null && endTime !== null) {
            startTimestamp = Timestamp.fromDate(new Date(startTime));
            endTimestamp = Timestamp.fromDate(new Date(endTime));
            baseQuery = query(
                leadsRef,
                where('createdAt', '>=', startTimestamp),
                where('createdAt', '<=', endTimestamp)
            );
        }


        // Apply user filters
        if (currentScreen === "Manager" && users.length > 0 && selectedUser?.length === 0) {
            baseQuery = query(baseQuery, where('assignedTo', 'in', users.map(user => user.id)));
        } else if (currentScreen === 'Staff' && users.length === 1 && selectedUser?.length === 0) {
            baseQuery = query(baseQuery, where('assignedTo', '==', users[0].id));
        } else if (selectedUser && selectedUser.length > 0) {
            baseQuery = query(baseQuery, where('assignedTo', '==', selectedUser[0].id));
        }

        if (sourceParameter) {
            baseQuery = query(baseQuery, where('sourceId', '==', sourceParameter));
        }

        // Get total leads and stage data using base query
        const totalLeads = await getCountFromServer(baseQuery);

        // Stage mapping
        const stageMap = new Map<string, number>();
        const stageQuery = query(stageRef);
        const stageSnapshot = await getDocs(stageQuery);
        stageSnapshot.forEach(doc => {
            stageMap.set(doc.id, 0);
        });

        // admission status mapping
        const admissionMap = new Map<string, number>();
        const admissionQuery = query(admissionRef);
        const admissionSnapshot = await getDocs(admissionQuery);
        admissionSnapshot.forEach(doc => {
            admissionMap.set(doc.id, 0);
        });

        // source mapping
        const sourceMap = new Map<string, number>();
        const sourceQuery = query(sourceRef);
        const sourceSnapshot = await getDocs(sourceQuery);
        sourceSnapshot.forEach(doc => {
            sourceMap.set(doc.id, 0);
        });

        // Campaign mapping
        const campaignMap = new Map<string, { count: number, stateMap: Map<string, number> }>();
        const campaignQuery = query(campaignRef);
        const campaignSnapshot = await getDocs(campaignQuery);
        campaignSnapshot.forEach(doc => {
            campaignMap.set(doc.id, { count: 0, stateMap: new Map<string, number>() });
        });

        // State mapping
        const stateMap = new Map<string, number>();
        const statePotentialMap = new Map<string, number>();
        const statePotentialArray = new Array<string>();
        const stateQuery = query(stateRef);
        const stateSnapshot = await getDocs(stateQuery);
        stateSnapshot.forEach(doc => {
            stateMap.set(doc.id, 0);
            // Check if state is potential ,ie isPotential is true
            if (doc.data().isPotential) {
                statePotentialArray.push(doc.id);
            }
        });

        // Get leads for stage mapping
        const leadSnapshot = await getDocs(baseQuery);
        leadSnapshot.forEach(doc => {
            const data = doc.data();

            // Helper function to normalize values and validate against snapshot map
            const normalizeValue = (value: any, validationMap: Map<string, any>) => {
                // Treat null, undefined, or empty string as undefined
                if (!value && value !== 0) return undefined;
                // Check if the value exists in the validation map
                return validationMap.has(value) ? value : undefined;
            };

            // Get and normalize state
            const state = normalizeValue(data?.stateId, stateMap);
            // if (state !== undefined) {
            const stateData = stateMap.get(state);
            // if (stateData !== undefined) {
            stateData && stateMap.set(state, stateData + 1);
            // }
            // }

            // Handle stage mapping
            const stage = normalizeValue(data?.stageId, stageMap);
            // if (stage !== undefined) {
            stageMap.set(stage, (stageMap.get(stage) || 0) + 1);
            // }

            // Handle admission status mapping
            const admissionStatus = normalizeValue(data?.admissionStatusId, admissionMap);
            // if (admissionStatus !== undefined) {
            admissionMap.set(admissionStatus, (admissionMap.get(admissionStatus) || 0) + 1);
            // }

            // Handle source mapping
            const source = normalizeValue(data?.sourceId, sourceMap);
            // if (source !== undefined) {
            sourceMap.set(source, (sourceMap.get(source) || 0) + 1);

            // Only update statePotentialMap if both source and state are valid
            if (statePotentialArray.includes(state)) {
                statePotentialMap.set(source, (statePotentialMap.get(source) || 0) + 1);
            }
            // }

            // Handle campaign mapping
            const campaign = normalizeValue(data?.campaignId, campaignMap);
            // if (campaign !== undefined) {
            const campaignData = campaignMap.get(campaign);
            if (campaignData) {
                campaignData.count++;
                // Only update stateMap if state is valid
                // if (state !== undefined) {
                campaignData.stateMap.set(state, (campaignData.stateMap.get(state) || 0) + 1);
                // }
                // }
            }
        });

        // Timeline mapping
        const timelineMap = new Map<string, { totalCount: number, stageCount: number }>();
        let diffInDays = 0;
        if (endTimestamp && startTimestamp) {
            diffInDays = differenceInDays(endTimestamp.toDate(), startTimestamp.toDate());
        }
        const isMonthlyView = diffInDays > 30;

        // Process base query for total counts in timeline
        leadSnapshot.forEach(doc => {
            const createdAt = (doc.data()?.createdAt as Timestamp).toDate();
            let timelineKey: string;

            if (isMonthlyView) {
                timelineKey = format(createdAt, 'MMM yyyy');
            } else {
                const weekNumber = getWeek(createdAt, { weekStartsOn: 1 });
                const weekStart = startOfWeek(createdAt, { weekStartsOn: 1 });
                const weekEnd = endOfWeek(createdAt, { weekStartsOn: 1 });
                const dateRange = `${format(weekStart, 'dd MMM')} - ${format(weekEnd, 'dd MMM')}`;
                timelineKey = `Week ${weekNumber}|${dateRange}`;
            }

            const currentValue = timelineMap.get(timelineKey) || { totalCount: 0, stageCount: 0 };
            currentValue.totalCount++;

            // If stageParameter exists, check if this lead matches the stage
            if (stageParameter && doc.data()?.stageId === stageParameter) {
                currentValue.stageCount++;
            }

            timelineMap.set(timelineKey, currentValue);
        });

        // Convert stage map to array
        const stageMapArray = Array.from(stageMap).map(([stage, count]) => {
            const stageDoc = stageSnapshot.docs.find(doc => doc.id === stage);
            const stageName = stageDoc?.data().stage || 'No Stage';
            return {
                stageId: stage,
                stage: stageName,
                count,
                percentage: count ? ((count / totalLeads.data().count) * 100).toFixed(1) + '%' : '0%'
            };
        });

        // Convert admission status map to array
        const admissionMapArray = Array.from(admissionMap).map(([admissionStatus, count]) => {
            const admissionDoc = admissionSnapshot.docs.find(doc => doc.id === admissionStatus);
            const admissionStatusName = admissionDoc?.data().admissionStatus || 'No Status';
            const level = admissionDoc?.data()?.level || 0;
            return {
                admissionStatusId: admissionStatus,
                admissionStatus: admissionStatusName,
                count,
                percentage: count ? ((count / totalLeads.data().count) * 100).toFixed(1) + '%' : '0%',
                level
            };
        });

        // Convert source map to array where state is in statePotentialArray and percentage is calculated based on total leads and potentialpercentage is calculated based on potentialCount
        const sourceMapArray: { sourceId: string, source: string, count: number, potentialCount: number, percentage: string, potentialpercentage: string }[] = [];

        const tempSourceMapArray = Array.from(sourceMap).map(([source, count]) => {
            const sourceDoc = sourceSnapshot.docs.find(doc => doc.id === source);
            const sourceName = sourceDoc?.data().source || 'No Source';

            const potentialCount = statePotentialMap.get(source
            ) || 0;

            return { sourceId: source, source: sourceName, count, potentialCount };
        });

        // Now define sourceMapArray
        sourceMapArray.push(...tempSourceMapArray.map(({ sourceId, source, count, potentialCount }) => {
            return {
                sourceId,
                source,
                count,
                potentialCount,
                percentage: ((count / totalLeads.data().count) * 100).toFixed(1) + '%',
                potentialpercentage: ((potentialCount / count) * 100).toFixed(1) + '%'
            };
        }));

        // Convert campaign map to array
        const campaignMapArray = Array.from(campaignMap).map(([campaign, { count, stateMap }]) => {
            const campaignDoc = campaignSnapshot.docs.find(doc => doc.id === campaign);
            const campaignName = campaignDoc?.data().campaign || 'No Campaign';
            const campaignCount = count;
            const statePercentageData = Array.from(stateMap).map(([state, count]) => {
                const stateDoc = stateSnapshot.docs.find(doc => doc.id === state);
                const stateName = stateDoc?.data().state || 'No State';
                const color = stateDoc?.data().color || '#000000';
                return {
                    stateId: state,
                    state: stateName,
                    color,
                    count,
                    percentage: ((count / campaignCount) * 100).toFixed(1) + '%'
                };
            });

            return {
                campaignId: campaign,
                campaign: campaignName,
                count: campaignCount,
                statePercentageData
            };
        });

        // Convert timeline map to array and sort
        const timelineMapArray = Array.from(timelineMap).map(([timeline, { totalCount, stageCount }]) => ({
            timeline,
            totalCount,
            ...(stageParameter && {
                stageId: stageParameter,
                stage: stageSnapshot.docs.find(doc => doc.id === stageParameter)?.data().stage || 'No Stage',
                stageCount
            })
        })).sort((a, b) => {
            if (isMonthlyView) {
                return compareAsc(parse(a.timeline, 'MMM yyyy', new Date()), parse(b.timeline, 'MMM yyyy', new Date()));
            } else {
                return parseInt(a.timeline.split(' ')[1]) - parseInt(b.timeline.split(' ')[1]);
            }
        });

        return {
            totalLeads: totalLeads.data().count,
            stageMapArray,
            admissionMapArray,
            sourceMapArray,
            campaignMapArray,
            timelineMapArray
        };
    } catch (error) {
        throw new Error(error instanceof Error ? error.message : 'Error fetching lead data');
    }
}

// Function to fetch lead stage analytics from leads collection
// In your Function file (where fetchLeadStageAnalytics is defined)
export async function fetchLeadStageAnalytics({
    db,
    userData,
    startTime,
    endTime,
    users,
    currentScreen,
    selectedUser,
    settingsData
}: FetchLeadDataParams): Promise<LeadData> {
    if (!userData?.companyId) {
        throw new Error('No company ID found');
    }
    try {
        const leadsRef = collection(db, `companies/${userData?.companyId}/leads`);
        const stageRef = collection(db, `companies/${userData?.companyId}/leadStages`);

        // Create base query
        let baseQuery: Query<DocumentData> = query(
            leadsRef,
            where('isActive', '==', true),
        );

        // Apply date filters if provided
        if (startTime !== null && endTime !== null) {
            const startTimestamp = Timestamp.fromDate(new Date(startTime));
            const endTimestamp = Timestamp.fromDate(new Date(endTime));
            baseQuery = query(
                baseQuery,
                where('createdAt', '>=', startTimestamp),
                where('createdAt', '<=', endTimestamp)
            );
        }

        // Apply user filters
        if (selectedUser && selectedUser.length > 0) {
            if (selectedUser.length > 1) {
                baseQuery = query(baseQuery, where('assignedTo', 'in', selectedUser.map(user => user.id)));
            } else {
                baseQuery = query(baseQuery, where('assignedTo', '==', selectedUser[0].id));
            }
        } else if (currentScreen === "Manager" && users.length > 0) {
            baseQuery = query(baseQuery, where('assignedTo', 'in', users.map(user => user.id)));
        } else if (currentScreen === 'Staff' && users.length === 1) {
            baseQuery = query(baseQuery, where('assignedTo', '==', users[0].id));
        }

        // Get total leads count
        const totalLeads = await getCountFromServer(baseQuery);

        // Get all stages
        const stageQuery = query(stageRef);
        const stageSnapshot = await getDocs(stageQuery);

        // Get counts for each stage
        const stageMapArray = await Promise.all(stageSnapshot.docs.map(async doc => {
            const stageId = doc.id;
            const stageCount = await getCountFromServer(
                query(baseQuery, where('stageId', '==', stageId))
            );
            return {
                stageId,
                stage: doc.data().stage,
                count: stageCount.data().count || 0,
                percentage: totalLeads.data().count > 0
                    ? ((stageCount.data().count / totalLeads.data().count) * 100).toFixed(1) + '%'
                    : '0%'
            };
        }));

        // Calculate leads with no stage
        const noStageCount = totalLeads.data().count - stageMapArray.reduce((acc, curr) => acc + curr.count, 0);

        return {
            totalLeads: totalLeads.data().count,
            stageMapArray: [
                ...stageMapArray,
                {
                    stageId: 'noStage',
                    stage: 'No Stage',
                    count: noStageCount,
                    percentage: totalLeads.data().count > 0
                        ? ((noStageCount / totalLeads.data().count) * 100).toFixed(1) + '%'
                        : '0%'
                }
            ]
        };
    } catch (error) {
        throw new Error(error instanceof Error ? error.message : 'Error fetching lead stage analytics');
    }
}
// Function to fetch lead stage timeline analytics from leads collection
export async function fetchLeadStageTimelineAnalytics({
    db,
    userData,
    startTime,
    endTime,
    users,
    currentScreen,
    selectedUser = [], // Default empty array if undefined
    stageParameter
}: FetchLeadDataParams & { selectedUser?: UserData[] }): Promise<LeadData> {
    try {
        const leadsRef = collection(db, `companies/${userData?.companyId}/leads`);
        const stageRef = collection(db, `companies/${userData?.companyId}/leadStages`);

        // Create base query without stage parameter
        let baseQueryConstraints = [
            where('isActive', '==', true)
        ];

        let startTimestamp: Timestamp | null = null;
        let endTimestamp: Timestamp | null = null;

        if (startTime !== null && endTime !== null) {
            startTimestamp = Timestamp.fromDate(new Date(startTime));
            endTimestamp = Timestamp.fromDate(new Date(endTime));

            baseQueryConstraints.push(
                where('createdAt', '>=', startTimestamp),
                where('createdAt', '<=', endTimestamp)
            );
        }

        // Apply user filters
        if (currentScreen === "Manager" && users.length > 0) {
            if (selectedUser.length > 0) {
                // If specific users are selected, use those
                baseQueryConstraints.push(where('assignedTo', 'in', selectedUser.map(user => user.id)));
            } else {
                // Otherwise use all users in the filtered set
                baseQueryConstraints.push(where('assignedTo', 'in', users.map(user => user.id)));
            }
        } else if (currentScreen === 'Staff' && users.length === 1) {
            baseQueryConstraints.push(where('assignedTo', '==', users[0].id));
        } else if (currentScreen === 'SubAgent' && users.length === 1) {
            baseQueryConstraints.push(where('subAgentId', '==', users[0].id));
        } else if (selectedUser.length > 0) {
            baseQueryConstraints.push(where('assignedTo', 'in', selectedUser.map(user => user.id)));
        }

        // Get total leads using getCountFromServer
        const baseQuery = query(leadsRef, ...baseQueryConstraints);
        const totalLeads = await getCountFromServer(baseQuery);

        // Determine view type
        let diffInDays = 0;
        if (endTimestamp && startTimestamp) {
            diffInDays = differenceInDays(endTimestamp.toDate(), startTimestamp.toDate());
        }
        const isMonthlyView = diffInDays > 30;

        // Prepare timeline queries
        const timelineMapArray: Array<{
            timeline: string;
            totalCount: number;
            stageId?: string;
            stageCount?: number;
        }> = [];

        if (isMonthlyView) {
            // Group by month
            if (startTimestamp && endTimestamp) {
                for (let d = new Date(startTimestamp.toDate()); d <= endTimestamp.toDate(); d.setMonth(d.getMonth() + 1)) {
                    const monthStart = startOfMonth(d);
                    const monthEnd = endOfMonth(d);

                    const monthQueryConstraints = [
                        ...baseQueryConstraints,
                        where('createdAt', '>=', Timestamp.fromDate(monthStart)),
                        where('createdAt', '<=', Timestamp.fromDate(monthEnd))
                    ];

                    const monthQuery = query(leadsRef, ...monthQueryConstraints);
                    const totalCount = await getCountFromServer(monthQuery);

                    let stageCount = 0;
                    if (stageParameter) {
                        console.log("stageParameter", stageParameter);

                        const stageQueryConstraints = [
                            ...monthQueryConstraints,
                            where('stageId', '==', stageParameter)
                        ];
                        const stageQuery = query(leadsRef, ...stageQueryConstraints);
                        const stageTotalCount = await getCountFromServer(stageQuery);
                        stageCount = stageTotalCount.data().count;
                    }

                    timelineMapArray.push({
                        timeline: format(monthStart, 'MMM yyyy'),
                        totalCount: totalCount.data().count,
                        ...(stageParameter ? {
                            stageId: stageParameter,
                            stageCount
                        } : {})
                    });
                }
            }
        } else {
            let startDate: Date;
            let endDate: Date;
            if (startTimestamp && endTimestamp) {
                startDate = startTimestamp.toDate();
                endDate = endTimestamp.toDate();
            } else {
                throw new Error('Start or end timestamp is missing');
            }
            let d = new Date(startDate);

            while (d <= endDate) {
                const weekStart = startOfWeek(d, { weekStartsOn: 1 });
                const weekEnd = endOfWeek(d, { weekStartsOn: 1 });

                // Adjust weekEnd to not exceed endDate
                const adjustedWeekEnd = weekEnd > endDate ? endDate : weekEnd;

                const weekQueryConstraints = [
                    ...baseQueryConstraints,
                    where('createdAt', '>=', Timestamp.fromDate(weekStart)),
                    where('createdAt', '<=', Timestamp.fromDate(adjustedWeekEnd))
                ];

                const weekQuery = query(leadsRef, ...weekQueryConstraints);
                const totalCount = await getCountFromServer(weekQuery);

                let stageCount = 0;
                if (stageParameter) {
                    const stageQueryConstraints = [
                        ...weekQueryConstraints,
                        where('stageId', '==', stageParameter)
                    ];
                    const stageQuery = query(leadsRef, ...stageQueryConstraints);
                    const stageTotalCount = await getCountFromServer(stageQuery);
                    stageCount = stageTotalCount.data().count;
                }

                const weekNumber = getWeek(weekStart, { weekStartsOn: 1 });
                const dateRange = `${format(weekStart, 'dd MMM')} - ${format(adjustedWeekEnd, 'dd MMM')}`;

                timelineMapArray.push({
                    timeline: `Week ${weekNumber}|${dateRange}`,
                    totalCount: totalCount.data().count,
                    ...(stageParameter ? {
                        stageId: stageParameter,
                        stageCount
                    } : {})
                });

                // Move to next week, ensuring we don't go beyond endDate
                d = addWeeks(weekStart, 1);
            }
        }

        // Sort timeline array
        const sortedTimelineMapArray = timelineMapArray.sort((a, b) => {
            if (isMonthlyView) {
                return compareAsc(parse(a.timeline, 'MMM yyyy', new Date()), parse(b.timeline, 'MMM yyyy', new Date()));
            } else {
                return parseInt(a.timeline.split(' ')[1]) - parseInt(b.timeline.split(' ')[1]);
            }
        });

        return {
            totalLeads: totalLeads.data().count,
            timelineMapArray: sortedTimelineMapArray
        };
    } catch (error) {
        throw new Error(error instanceof Error ? error.message : 'Error fetching lead data');
    }
}

// Function to fetch lead admission analytics from leads collection
export async function fetchLeadAdmissionAnalytics({
    db,
    userData,
    startTime,
    endTime,
    users,
    currentScreen,
    selectedUser,
    settingsData
}: FetchLeadDataParams): Promise<LeadData> {
    if (!userData?.companyId) {
        throw new Error('No company ID found');
    }
    try {
        const leadsRef = collection(db, `companies/${userData?.companyId}/leads`);
        const admissionRef = collection(db, `companies/${userData?.companyId}/leadAdmissionStatus`);

        // Create base query with active leads filter
        let baseQuery: Query<DocumentData> = query(
            leadsRef,
            where('isActive', '==', true)
        );

        // Apply date range filter if provided
        if (startTime !== null && endTime !== null) {
            const startTimestamp = Timestamp.fromDate(new Date(startTime));
            const endTimestamp = Timestamp.fromDate(new Date(endTime));
            baseQuery = query(
                baseQuery,
                where('createdAt', '>=', startTimestamp),
                where('createdAt', '<=', endTimestamp)
            );
        }

        // Enhanced user filtering logic to handle department-wide selection
        if (selectedUser && selectedUser.length > 0) {
            // Handle multiple selected users (when department is selected)
            if (selectedUser.length > 1) {
                baseQuery = query(baseQuery, where('assignedTo', 'in', selectedUser.map(user => user.id)));
            }
            // Handle single selected user
            else {
                baseQuery = query(baseQuery, where('assignedTo', '==', selectedUser[0].id));
            }
        }
        // Default filters based on user role
        else if (currentScreen === "Manager" && users.length > 0) {
            baseQuery = query(baseQuery, where('assignedTo', 'in', users.map(user => user.id)));
        } else if (currentScreen === 'Staff' && users.length === 1) {
            baseQuery = query(baseQuery, where('assignedTo', '==', users[0].id));
        } else if (currentScreen === 'SubAgent' && users.length === 1) {
            baseQuery = query(baseQuery, where('subAgentId', '==', users[0].id));
        }

        // Get total leads count
        const totalLeads = await getCountFromServer(baseQuery);

        // Fetch all admission statuses
        const admissionQuery = query(admissionRef);
        const admissionSnapshot = await getDocs(admissionQuery);

        // Calculate counts for each admission status
        const admissionMapArray = await Promise.all(admissionSnapshot.docs.map(async doc => {
            const admissionStatusId = doc.id;
            const statusCount = await getCountFromServer(
                query(baseQuery, where('admissionStatusId', '==', admissionStatusId))
            );

            return {
                admissionStatusId,
                admissionStatus: doc.data().admissionStatus,
                count: statusCount.data().count,
                percentage: totalLeads.data().count > 0
                    ? `${((statusCount.data().count / totalLeads.data().count) * 100).toFixed(1)}%`
                    : '0%',
                level: doc.data().level || 0
            };
        }));

        // Calculate leads with no admission status
        const noStatusCount = totalLeads.data().count - admissionMapArray.reduce((acc, curr) => acc + curr.count, 0);
        admissionMapArray.push({
            admissionStatusId: 'noStatus',
            admissionStatus: 'No Status',
            count: noStatusCount,
            percentage: totalLeads.data().count > 0
                ? `${((noStatusCount / totalLeads.data().count) * 100).toFixed(1)}%`
                : '0%',
            level: 0
        });

        return {
            chartData: [], // Add an empty array or appropriate chart data as needed
            totalLeads: totalLeads.data().count,
            admissionMapArray: admissionMapArray.sort((a, b) => b.level - a.level) // Sort by level
        };
    }
    catch (error) {
        throw new Error(error instanceof Error ? error.message : 'Error fetching lead admission analytics');
    }
}

// Function to fetch lead campaign analytics from leads collection
export async function fetchLeadCampaignAnalytics({
    db,
    userData,
    startTime,
    endTime,
    users,
    currentScreen,
    selectedUser,
    settingsData
}: FetchLeadDataParams): Promise<LeadData> {
    if (!userData?.companyId) {
        throw new Error('No company ID found');
    }
    try {
        const leadsRef = collection(db, `companies/${userData?.companyId}/leads`);
        const campaignRef = collection(db, `companies/${userData?.companyId}/leadCampaigns`);
        const stateRef = collection(db, `companies/${userData?.companyId}/leadStates`);



        // Create base query without stage parameter
        let baseQuery: Query<DocumentData> = query(
            leadsRef,
            where('isActive', '==', true)
        );

        if (startTime !== null && endTime !== null) {
            const startTimestamp = Timestamp.fromDate(new Date(startTime));
            const endTimestamp = Timestamp.fromDate(new Date(endTime));
            baseQuery = query(
                leadsRef,
                where('createdAt', '>=', startTimestamp),
                where('createdAt', '<=', endTimestamp)
            );
        }

        // Apply user filters
        if (currentScreen === "Manager" && users.length > 0 && selectedUser?.length === 0) {
            baseQuery = query(baseQuery, where('assignedTo', 'in', users.map(user => user.id)));
        }
        else if (currentScreen === 'Staff' && users.length === 1 && selectedUser?.length === 0) {
            baseQuery = query(baseQuery, where('assignedTo', '==', users[0].id));
        }
        else if (selectedUser && selectedUser.length > 0) {
            baseQuery = query(baseQuery, where('assignedTo', '==', selectedUser[0].id));
        }

        // Get total leads and stage data using base query
        const totalLeads = await getCountFromServer(baseQuery);

        // Campaign mapping
        const campaignMap = new Map<string, { count: number, stateMap: Map<string, number> }>();
        const campaignQuery = query(campaignRef);
        const campaignSnapshot = await getDocs(campaignQuery);
        settingsData.forEach((doc: { id: string; }) => {
            campaignMap.set(doc.id, { count: 0, stateMap: new Map<string, number>() });
        });

        // State mapping
        const stateMap = new Map<string, number>();
        const stateQuery = query(stateRef);
        const stateSnapshot = await getDocs(stateQuery);
        stateSnapshot.forEach(doc => {
            stateMap.set(doc.id, 0);
        });

        // Get leads for campaign mapping by getting count from server for each campaign
        const campaignMapArray = await Promise.all(campaignSnapshot.docs.map(async doc => {
            const campaignId = doc.id;
            const campaignCount = await getCountFromServer(query(baseQuery, where('campaignId', '==', campaignId)));

            // Get leads for state mapping by getting count from server for each state
            const stateSnapshot = await getDocs(stateQuery);
            const stateMap = new Map(); // Create new map for each campaign

            // Use Promise.all with map instead of forEach for state operations
            await Promise.all(stateSnapshot.docs.map(async stateDoc => {
                const stateId = stateDoc.id;
                const stateCount = await getCountFromServer(query(baseQuery,
                    where('campaignId', '==', campaignId),
                    where('stateId', '==', stateId)
                ));
                stateMap.set(stateId, stateCount.data().count);
            }));

            const statePercentageData = Array.from(stateMap).map(([state, count]) => {
                const stateDoc = stateSnapshot.docs.find(doc => doc.id === state);
                const stateName = stateDoc?.data().state || 'No State';
                const color = stateDoc?.data().color || '#000000';
                return {
                    stateId: state || 'noState',
                    state: stateName || 'No State',
                    color,
                    count,
                    percentage: count ? ((count / campaignCount.data().count) * 100).toFixed(1) + '%' : '0%'
                };
            });

            // include no state in statePercentageData
            const noStateCount = campaignCount.data().count - statePercentageData.reduce((acc, curr) => acc + curr.count, 0);
            statePercentageData.push({
                stateId: 'noState',
                state: 'No State',
                color: '#000000',
                count: noStateCount,
                percentage: noStateCount ? ((noStateCount / campaignCount.data().count) * 100).toFixed(1) + '%' : '0%'
            });

            return {
                campaignId,
                campaign: doc.data().campaign,
                count: campaignCount.data().count,
                statePercentageData: statePercentageData.sort((a, b) => {
                    // If either item is "No State", it should go to the end
                    if (a.state === "No State") return 1;
                    if (b.state === "No State") return -1;

                    // For other states, sort in descending order
                    return b.state - a.state;
                })
            };
        }));

        return {
            totalLeads: totalLeads.data().count,
            campaignMapArray
        };
    }
    catch (error) {
        throw new Error(error instanceof Error ? error.message : 'Error fetching lead campaign analytics');
    }
}

// Function to fetch lead source analytics from leads collection
export async function fetchLeadSourceAnalytics({
    db,
    userData,
    startTime,
    endTime,
    users,
    currentScreen,
    selectedUser,
    settingsData
}: FetchLeadDataParams): Promise<LeadData> {
    if (!userData?.companyId) {
        throw new Error('No company ID found');
    }
    try {
        const leadsRef = collection(db, `companies/${userData?.companyId}/leads`);
        const sourceRef = collection(db, `companies/${userData?.companyId}/leadSources`);
        const stateRef = collection(db, `companies/${userData?.companyId}/leadStates`);

        // Create base query without stage parameter
        let baseQuery: Query<DocumentData> = query(
            leadsRef,
            where('isActive', '==', true)
        );

        if (startTime !== null && endTime !== null) {
            const startTimestamp = Timestamp.fromDate(new Date(startTime));
            const endTimestamp = Timestamp.fromDate(new Date(endTime));
            baseQuery = query(
                leadsRef,
                where('createdAt', '>=', startTimestamp),
                where('createdAt', '<=', endTimestamp)
            );
        }

        // Apply user filters
        if (currentScreen === "Manager" && users.length > 0 && selectedUser?.length === 0) {
            baseQuery = query(baseQuery, where('assignedTo', 'in', users.map(user => user.id)));
        }
        else if (currentScreen === 'Staff' && users.length === 1 && selectedUser?.length === 0) {
            baseQuery = query(baseQuery, where('assignedTo', '==', users[0].id));
        }
        else if (selectedUser && selectedUser.length > 0) {
            baseQuery = query(baseQuery, where('assignedTo', '==', selectedUser[0].id));
        }

        // Get total leads and stage data using base query
        const totalLeads = await getCountFromServer(baseQuery);

        // source mapping
        const sourceQuery = query(sourceRef);
        const sourceSnapshot = await getDocs(sourceQuery);

        // State mapping
        const stateMap = new Map<string, number>();
        const stateQuery = query(stateRef);
        const stateSnapshot = await getDocs(stateQuery);

        // Source mapping
        const sourceMap = new Map<string, number>();

        // Get leads for source mapping by getting count from server for each source
        const sourceMapArray = await Promise.all(sourceSnapshot.docs.map(async doc => {
            const sourceId = doc.id;
            const sourceCount = await getCountFromServer(query(baseQuery, where('sourceId', '==', sourceId)));

            // Get leads for state mapping by getting count from server for each state
            const stateSnapshot = await getDocs(stateQuery);
            const stateMap = new Map(); // Create new map for each source

            // Use Promise.all with map instead of forEach for state operations
            await Promise.all(stateSnapshot.docs.map(async stateDoc => {
                const stateId = stateDoc.id;
                const stateCount = await getCountFromServer(query(baseQuery,
                    where('sourceId', '==', sourceId),
                    where('stateId', '==', stateId)
                ));
                stateMap.set(stateId, stateCount.data().count);
            }));

            const statePotentialArray = Array.from(stateMap).filter(([stateId, count]) => {
                const stateDoc = stateSnapshot.docs.find(doc => doc.id === stateId);
                return stateDoc?.data().isPotential;
            });

            const potentialCount = statePotentialArray.reduce((acc, [stateId, count]) => acc + count, 0);

            return {
                sourceId,
                source: doc.data().source,
                count: sourceCount.data().count,
                potentialCount,
                percentage: sourceCount.data().count ? ((sourceCount.data().count / totalLeads.data().count) * 100).toFixed(1) + '%' : '0%',
                potentialpercentage: potentialCount ? ((potentialCount / sourceCount.data().count) * 100).toFixed(1) + '%' : '0%'
            };
        }));

        // include no source in sourceMapArray
        await Promise.all(stateSnapshot.docs.map(async stateDoc => {
            const stateId = stateDoc.id;
            const stateCount = await getCountFromServer(query(baseQuery,
                where('stateId', '==', stateId)
            ));
            stateMap.set(stateId, stateCount.data().count);
        }));

        const statePotentialArray = Array.from(stateMap).filter(([stateId, count]) => {
            const stateDoc = stateSnapshot.docs.find(doc => doc.id === stateId);
            return stateDoc?.data().isPotential;
        });

        const potentialCount = statePotentialArray.reduce((acc, [stateId, count]) => acc + count, 0);

        const noSourceCount = totalLeads.data().count - sourceMapArray.reduce((acc, curr) => acc + curr.count, 0);
        const noSourcePotentialCount = potentialCount - sourceMapArray.reduce((acc, curr) => acc + curr.potentialCount, 0);
        sourceMapArray.push({
            sourceId: 'noSource',
            source: 'No Source',
            count: noSourceCount,
            potentialCount: noSourcePotentialCount,
            percentage: noSourceCount ? ((noSourceCount / totalLeads.data().count) * 100).toFixed(1) + '%' : '0%',
            potentialpercentage: noSourcePotentialCount ? ((noSourcePotentialCount / noSourceCount) * 100).toFixed(1) + '%' : '0%'
        });

        return {
            totalLeads: totalLeads.data().count,
            sourceMapArray
        };

    }
    catch (error) {
        throw new Error(error instanceof Error ? error.message : 'Error fetching lead source analytics');
    }
}

// Helper function to count unique leads called by a user
interface FetchUniqueCallsCountParams {
    db: Firestore;
    userData: User | null;
    startTime: string | null;
    endTime: string | null;
    userId: string;
}

async function fetchUniqueCallsCount({
    db,
    userData,
    startTime,
    endTime,
    userId
}: FetchUniqueCallsCountParams): Promise<number> {
    if (!userData?.companyId) {
        return 0;
    }

    try {
        const callLogsRef = collection(db, `companies/${userData.companyId}/callLogs`);
        
        // Create base query for this specific user
        let baseQuery = query(
            callLogsRef,
            where('userId', '==', userId)
        );

        // Apply date filters if provided
        if (startTime && endTime) {
            const startTimestamp = Timestamp.fromDate(new Date(startTime));
            const endTimestamp = Timestamp.fromDate(new Date(endTime));
            baseQuery = query(
                baseQuery,
                where('callTimestamp', '>=', startTimestamp),
                where('callTimestamp', '<', endTimestamp)
            );
        }

        const querySnapshot = await getDocs(baseQuery);
        
        // Extract unique leadUids from the call logs
        const uniqueLeadIds = new Set<string>();
        
        querySnapshot.docs.forEach(doc => {
            const callData = doc.data();
            if (callData.leadUid) {
                uniqueLeadIds.add(callData.leadUid);
            }
        });

        return uniqueLeadIds.size;
    } catch (error) {
        console.error('Error fetching unique calls count:', error);
        return 0;
    }
}

// Function to fetch detailed call logs for Excel export
interface FetchCallLogsForExportParams {
    db: Firestore;
    userData: User | null;
    startTime: string | null;
    endTime: string | null;
    users: UserData[];
    currentScreen: string;
    selectedUser: UserData[] | null;
    currentUserId?: string;
}

interface ConsolidatedCallData {
    userId: string;
    userName: string;
    totalOutboundCalls: number;
    totalInboundCalls: number;
    totalCalls: number;
    connectedCalls: number;
    missedCalls: number;
    uniqueCalls: number; // unique leads called (each lead counted once)
    totalDuration: number; // in seconds
    outboundDuration: number; // in seconds
    inboundDuration: number; // in seconds
    averageCallDurationTotal: number; // in seconds - includes all calls
    averageCallDurationConnected: number; // in seconds - connected calls only
    connectionRate: string; // percentage
    callAttemptTarget: number;
    targetAchievement: string; // percentage
}

export async function fetchCallLogsForExport({
    db,
    userData,
    startTime,
    endTime,
    users,
    currentScreen,
    selectedUser,
    currentUserId
}: FetchCallLogsForExportParams): Promise<ConsolidatedCallData[]> {
    if (!userData?.companyId) {
        return [];
    }

    try {
        // Calculate days in range
        let daysInRange = 1;
        if (startTime !== null && endTime !== null) {
            const startDate = new Date(startTime);
            const endDate = new Date(endTime);
            daysInRange = differenceInDays(endDate, startDate) + 1;
        }

        // Determine which users to process
        let targetUsers: UserData[] = [];

        if (currentScreen === 'Staff') {
            const staffUser = users.find(user => user.id === currentUserId);
            if (staffUser) {
                targetUsers = [staffUser];
            }
        } else if (selectedUser && selectedUser.length > 0) {
            targetUsers = users.filter(user =>
                selectedUser.some(selected => selected.id === user.id)
            );
        } else {
            targetUsers = users;
        }

        const consolidatedData: ConsolidatedCallData[] = [];

        for (const user of targetUsers) {
            const userCallMetrics = await fetchCallMetrics({
                db,
                userData,
                startTime,
                endTime,
                users: [user],
                currentScreen,
                selectedUser: [{ id: user.id } as UserData]
            });

            // Fetch unique calls count for this user
            const uniqueCallsCount = await fetchUniqueCallsCount({
                db,
                userData,
                startTime,
                endTime,
                userId: user.id
            });

            const totalCalls = userCallMetrics.outboundCalls + userCallMetrics.inboundCalls;
            const adjustedTarget = (userCallMetrics.callAttemptTarget / daysInRange) * daysInRange;

            const connectionRate = userCallMetrics.outboundCalls > 0
                ? ((userCallMetrics.connected / userCallMetrics.outboundCalls) * 100).toFixed(1)
                : '0.0';

            const targetAchievement = adjustedTarget > 0
                ? ((userCallMetrics.outboundCalls / adjustedTarget) * 100).toFixed(1)
                : '0.0';

            const averageCallDurationTotal = totalCalls > 0
                ? Math.round(userCallMetrics.totalDuration / totalCalls)
                : 0;

            const averageCallDurationConnected = userCallMetrics.connected > 0
                ? Math.round(userCallMetrics.totalDuration / userCallMetrics.connected)
                : 0;

            consolidatedData.push({
                userId: user.id,
                userName: (user as any).name || 'Unknown User',
                totalOutboundCalls: userCallMetrics.outboundCalls,
                totalInboundCalls: userCallMetrics.inboundCalls,
                totalCalls: totalCalls,
                connectedCalls: userCallMetrics.connected,
                missedCalls: userCallMetrics.missedCalls,
                uniqueCalls: uniqueCallsCount,
                totalDuration: userCallMetrics.totalDuration,
                outboundDuration: userCallMetrics.outboundDuration,
                inboundDuration: userCallMetrics.inboundDuration,
                averageCallDurationTotal,
                averageCallDurationConnected,
                connectionRate: `${connectionRate}%`,
                callAttemptTarget: adjustedTarget,
                targetAchievement: `${targetAchievement}%`
            });
        }

        return consolidatedData;
    } catch (error) {
        console.error('Error fetching call logs for export:', error);
        return [];
    }
}

